import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PlaylistAddCheck as ReviewIcon,
  Save as SaveIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { getDependencyColor, summarizeProviderStatus } from '../utils/translationUi';

const DEFAULT_TRANSLATION_AUDIO_MODE = 'REPLACE_ORIGINAL_AUDIO';
const AUTH_CONFIG_UPDATED_EVENT = 'scammerDetectionAuthConfigUpdated';
const CONFIG_NAVIGATION_GUARD_KEY = '__scammerDetectionConfirmNavigation';
const TRANSLATION_AUDIO_MODE_OPTIONS = [
  { value: 'REPLACE_ORIGINAL_AUDIO', label: 'Replace Original Audio' },
  { value: 'MIX_WITH_ORIGINAL_AUDIO', label: 'Mix With Original Audio' },
];
const THEME_PROFILE_OPTIONS = [
  { value: 'enterprise-admin', label: 'Enterprise Admin', description: 'Neutral gray workspace with blue accents and dark sidebar' },
  { value: 'classic-dark', label: 'Classic Dark', description: 'Dark operational theme for low-light monitoring' },
  { value: 'high-contrast', label: 'High Contrast', description: 'Maximum contrast for accessibility and NOC-style visibility' },
];
const DEFAULT_THEME_PROFILE = 'enterprise-admin';
const CALLEE_IDENTIFIER_PATTERN = /^(?:[+]?[0-9]{7,15}|[0-9]{2,6})$/;
const LANGUAGE_CODE_PATTERN = /^[a-z]{2}(?:-[A-Z]{2})?$/;
const CSV_FIELD_ALIASES = {
  calleeNumber: ['calleenumber', 'callee', 'number', 'extension', 'sipnumber'],
  translationEnabled: ['translationenabled', 'enabled', 'translation', 'ai2enabled'],
  preferredTargetLanguage: ['preferredtargetlanguage', 'targetlanguage', 'language', 'target'],
  preferredTtsVoice: ['preferredttsvoice', 'ttsvoice', 'voice'],
  translationAudioMode: ['translationaudiomode', 'audiomode', 'mode'],
  licenseLevel: ['licenselevel', 'license', 'ailevel', 'tier'],
};
const PROFILE_COMPARISON_FIELDS = [
  'translationEnabled',
  'preferredTargetLanguage',
  'preferredTtsVoice',
  'translationAudioMode',
  'licenseLevel',
];
const BULK_ACTION_COLORS = {
  CREATE: 'success',
  UPDATE: 'warning',
  NO_CHANGE: 'default',
  ERROR: 'error',
};
const BULK_IMPORT_SAMPLE_CSV = [
  'calleeNumber,translationEnabled,preferredTargetLanguage,preferredTtsVoice,translationAudioMode,licenseLevel',
  '6070,true,th-TH,,REPLACE_ORIGINAL_AUDIO,AI2',
  '+66812345678,false,en-US,en-US-Neural2-F,MIX_WITH_ORIGINAL_AUDIO,AI1',
].join('\n');
const BULK_IMPORT_SAMPLE_CSV_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(BULK_IMPORT_SAMPLE_CSV)}`;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.includes(',') ? result.split(',').pop() : result);
  };
  reader.onerror = () => reject(reader.error || new Error('Could not read audio file'));
  reader.readAsDataURL(file);
});

const normalizeTranslationAudioMode = (value) => (
  TRANSLATION_AUDIO_MODE_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_TRANSLATION_AUDIO_MODE
);

const normalizeThemeProfile = (value) => (
  THEME_PROFILE_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_THEME_PROFILE
);

const normalizeCsvHeader = (value) => String(value || '')
  .trim()
  .replace(/[\s_-]+/g, '')
  .toLowerCase();

const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        field += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      row.push(field);
      field = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      continue;
    }

    field += char;
  }

  row.push(field);
  rows.push(row);

  return rows.filter((csvRow) => csvRow.some((value) => String(value || '').trim() !== ''));
};

const parseCsvText = (text) => {
  const csvRows = parseCsvRows(text);
  if (csvRows.length === 0) {
    return [];
  }

  const headers = csvRows[0].map(normalizeCsvHeader);
  return csvRows.slice(1).map((values, index) => {
    const raw = headers.reduce((acc, header, headerIndex) => {
      if (header) {
        acc[header] = values[headerIndex] || '';
      }
      return acc;
    }, {});

    return {
      lineNumber: index + 2,
      raw,
    };
  });
};

const getCsvValue = (raw, fieldName) => {
  const aliases = CSV_FIELD_ALIASES[fieldName] || [];
  const key = aliases.find((alias) => raw[alias] !== undefined);
  return key ? String(raw[key]).trim() : '';
};

const parseImportBoolean = (value, defaultValue = true) => {
  if (value === '') {
    return { value: defaultValue, error: null };
  }

  const normalized = value.toLowerCase();
  if (['true', '1', 'yes', 'y', 'enabled', 'enable'].includes(normalized)) {
    return { value: true, error: null };
  }
  if (['false', '0', 'no', 'n', 'disabled', 'disable'].includes(normalized)) {
    return { value: false, error: null };
  }

  return { value: defaultValue, error: 'translationEnabled must be true or false' };
};

const normalizeLanguageCode = (value) => {
  const trimmed = String(value || 'th-TH').trim();
  const parts = trimmed.split('-');
  if (parts.length === 2) {
    return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
  }
  return trimmed.toLowerCase();
};

const normalizeImportAudioMode = (value) => {
  const normalized = String(value || DEFAULT_TRANSLATION_AUDIO_MODE).trim().toUpperCase();
  if (normalized === 'REPLACE' || normalized === 'REPLACE_ORIGINAL') {
    return 'REPLACE_ORIGINAL_AUDIO';
  }
  if (normalized === 'MIX' || normalized === 'MIX_ORIGINAL') {
    return 'MIX_WITH_ORIGINAL_AUDIO';
  }
  return normalized;
};

const normalizeImportProfile = (parsedRow) => {
  const errors = [];
  const booleanResult = parseImportBoolean(getCsvValue(parsedRow.raw, 'translationEnabled'), true);
  const calleeNumber = getCsvValue(parsedRow.raw, 'calleeNumber');
  const preferredTargetLanguage = normalizeLanguageCode(
    getCsvValue(parsedRow.raw, 'preferredTargetLanguage') || 'th-TH'
  );
  const preferredTtsVoice = getCsvValue(parsedRow.raw, 'preferredTtsVoice');
  const translationAudioMode = normalizeImportAudioMode(getCsvValue(parsedRow.raw, 'translationAudioMode'));
  const licenseLevel = (getCsvValue(parsedRow.raw, 'licenseLevel') || 'AI2').toUpperCase();

  if (!calleeNumber) {
    errors.push('calleeNumber is required');
  } else if (!CALLEE_IDENTIFIER_PATTERN.test(calleeNumber)) {
    errors.push('calleeNumber must be E.164 or a 2-6 digit extension');
  }

  if (booleanResult.error) {
    errors.push(booleanResult.error);
  }

  if (!LANGUAGE_CODE_PATTERN.test(preferredTargetLanguage)) {
    errors.push('preferredTargetLanguage must look like th-TH');
  }

  if (!TRANSLATION_AUDIO_MODE_OPTIONS.some((option) => option.value === translationAudioMode)) {
    errors.push('translationAudioMode is not supported');
  }

  if (!['AI1', 'AI2'].includes(licenseLevel)) {
    errors.push('licenseLevel must be AI1 or AI2');
  }

  return {
    lineNumber: parsedRow.lineNumber,
    data: {
      calleeNumber,
      translationEnabled: booleanResult.value,
      preferredTargetLanguage,
      preferredTtsVoice: preferredTtsVoice || null,
      translationAudioMode,
      licenseLevel,
    },
    errors,
  };
};

const toComparableProfile = (profile) => ({
  translationEnabled: profile?.translationEnabled !== false,
  preferredTargetLanguage: profile?.preferredTargetLanguage || 'th-TH',
  preferredTtsVoice: profile?.preferredTtsVoice || '',
  translationAudioMode: normalizeTranslationAudioMode(profile?.translationAudioMode),
  licenseLevel: profile?.license?.licenseLevel || profile?.licenseLevel || 'AI1',
});

const canApplyBulkImportRow = (row) => ['CREATE', 'UPDATE'].includes(row.action) && row.errors.length === 0;

const buildConfigurationFormData = (config = {}) => ({
  scammerThreshold: parseFloat(config.scammerThreshold ?? 0.7),
  scammerAction: config.scammerAction || 'WHISPER',
  whisperEnabled: config.whisperEnabled === true || config.whisperEnabled === 'true',
  whisperAudioPath: config.whisperAudioPath || '/app/audio/whisper-warning.wav',
  recordingDisclaimerEnabled: config.recordingDisclaimerEnabled === true || config.recordingDisclaimerEnabled === 'true',
  recordingDisclaimerAudioPath: config.recordingDisclaimerAudioPath || '',
  recordingDisclaimerAudioUri: config.recordingDisclaimerAudioUri || '',
  recordingDisclaimerFileName: config.recordingDisclaimerFileName || '',
  translationAudioMode: normalizeTranslationAudioMode(config.translationAudioMode),
  translationTargetLanguage: config.translationTargetLanguage || 'th-TH',
  translationStoreTranscripts: config.translationStoreTranscripts === true || config.translationStoreTranscripts === 'true',
  translationTranscriptRetentionHours: parseInt(config.translationTranscriptRetentionHours ?? 24, 10),
  sessionInactivityTimeoutMinutes: parseInt(config.sessionInactivityTimeoutMinutes ?? 30, 10),
  themeProfile: normalizeThemeProfile(config.themeProfile),
  apiTimeout: parseInt(config.apiTimeout ?? 5000, 10),
  maxReportCount: parseInt(config.maxReportCount ?? 100, 10),
});

const configurationFormsMatch = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export default function Configuration() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery('config', api.getConfiguration);
  const { data: health } = useQuery('health', api.getHealth, {
    retry: 1,
    refetchInterval: 30000,
  });

  const [formData, setFormData] = useState(() => buildConfigurationFormData());
  const [savedFormData, setSavedFormData] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const isDirty = React.useMemo(
    () => savedFormData !== null && !configurationFormsMatch(formData, savedFormData),
    [formData, savedFormData]
  );
  const isDirtyRef = React.useRef(isDirty);

  React.useEffect(() => {
    if (config) {
      const nextFormData = buildConfigurationFormData(config);
      setSavedFormData(nextFormData);
      if (!isDirtyRef.current) {
        setFormData(nextFormData);
      }
    }
  }, [config]);

  React.useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const confirmDiscardUnsavedChanges = React.useCallback(() => (
    !isDirty || window.confirm('You have unsaved configuration changes. Discard them and leave this page?')
  ), [isDirty]);

  React.useEffect(() => {
    window[CONFIG_NAVIGATION_GUARD_KEY] = confirmDiscardUnsavedChanges;
    return () => {
      if (window[CONFIG_NAVIGATION_GUARD_KEY] === confirmDiscardUnsavedChanges) {
        delete window[CONFIG_NAVIGATION_GUARD_KEY];
      }
    };
  }, [confirmDiscardUnsavedChanges]);

  React.useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const updateMutation = useMutation(
    (data) => api.updateConfiguration(data),
    {
      onSuccess: (updatedConfig) => {
        const nextFormData = buildConfigurationFormData(updatedConfig);
        queryClient.setQueryData('config', updatedConfig);
        setSavedFormData(nextFormData);
        setFormData(nextFormData);
        setLastSavedAt(new Date());
        window.dispatchEvent(new Event(AUTH_CONFIG_UPDATED_EVENT));
        setSnackbar({
          open: true,
          message: 'Configuration saved successfully',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const changePasswordMutation = useMutation(
    () => api.changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    }),
    {
      onSuccess: () => {
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSnackbar({
          open: true,
          message: 'Password changed successfully',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.error || error.message,
          severity: 'error',
        });
      },
    }
  );

  const passwordMismatch = passwordForm.confirmPassword.length > 0 &&
    passwordForm.confirmPassword !== passwordForm.newPassword;

  const uploadDisclaimerMutation = useMutation(
    async (file) => api.uploadRecordingDisclaimerAudio({
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      contentBase64: await fileToBase64(file),
    }),
    {
      onSuccess: (result) => {
        setFormData((previous) => ({
          ...previous,
          recordingDisclaimerEnabled: true,
          recordingDisclaimerAudioPath: result.recordingDisclaimerAudioPath || previous.recordingDisclaimerAudioPath,
          recordingDisclaimerAudioUri: result.recordingDisclaimerAudioUri || previous.recordingDisclaimerAudioUri,
          recordingDisclaimerFileName: result.recordingDisclaimerFileName || previous.recordingDisclaimerFileName,
        }));
        setSnackbar({
          open: true,
          message: 'Recording disclaimer uploaded. Save changes to activate it for new calls.',
          severity: 'info',
        });
      },
      onError: (error) => {
        const serverMsg = error.response?.data?.error?.message || error.response?.data?.error;
        setSnackbar({
          open: true,
          message: serverMsg ? `Upload failed: ${serverMsg}` : `Upload failed: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const [profileForm, setProfileForm] = useState({
    calleeNumber: '',
    translationEnabled: true,
    preferredTargetLanguage: 'th-TH',
    preferredTtsVoice: '',
    translationAudioMode: DEFAULT_TRANSLATION_AUDIO_MODE,
    licenseLevel: 'AI2',
  });
  const [inlineEditingCallee, setInlineEditingCallee] = useState(null);
  const [inlineEditForm, setInlineEditForm] = useState({
    translationEnabled: true,
    preferredTargetLanguage: 'th-TH',
    translationAudioMode: DEFAULT_TRANSLATION_AUDIO_MODE,
    licenseLevel: 'AI1',
  });
  const [bulkImportFileName, setBulkImportFileName] = useState('');
  const [bulkImportRows, setBulkImportRows] = useState([]);
  const [bulkImportApplying, setBulkImportApplying] = useState(false);
  const [bulkImportReviewOpen, setBulkImportReviewOpen] = useState(false);

  const { data: calleeProfilesResponse, isLoading: isProfilesLoading } = useQuery(
    'calleeProfiles',
    () => api.getCalleeProfiles({ limit: 100, offset: 0 }),
    {
      retry: 1,
    }
  );

  const calleeProfiles = React.useMemo(
    () => calleeProfilesResponse?.items || [],
    [calleeProfilesResponse]
  );
  const calleeProfileLookup = React.useMemo(
    () => new Map(calleeProfiles.map((profile) => [profile.calleeNumber, profile])),
    [calleeProfiles]
  );

  const saveCalleeProfilePayload = React.useCallback(async (payload) => {
    const raw = payload.data || {};
    const licenseLevel = raw.licenseLevel || null;

    const profileData = {
      translationEnabled: raw.translationEnabled !== false,
      preferredTargetLanguage: raw.preferredTargetLanguage || 'th-TH',
      translationAudioMode: normalizeTranslationAudioMode(raw.translationAudioMode),
      ...(raw.preferredTtsVoice !== undefined ? { preferredTtsVoice: raw.preferredTtsVoice || null } : {}),
    };

    if (payload.isUpdate) {
      await api.updateCalleeProfile(payload.calleeNumber, profileData);
      if (licenseLevel) {
        await api.updateCalleeLicense(payload.calleeNumber, { licenseLevel });
      }
      return api.getCalleeProfile(payload.calleeNumber);
    }

    const created = await api.createCalleeProfile({
      calleeNumber: payload.calleeNumber,
      ...profileData,
      licenseLevel,
    });

    if (licenseLevel) {
      await api.updateCalleeLicense(payload.calleeNumber, { licenseLevel });
    }

    return created;
  }, []);

  const saveProfileMutation = useMutation(
    async (payload) => {
      const raw = payload.data || {};
      const licenseLevel = raw.licenseLevel || null;

      // Build a clean profile payload — coerce translationEnabled to boolean,
      // exclude licenseLevel (handled via dedicated license endpoint).
      const profileData = {
        translationEnabled: raw.translationEnabled !== false,
        preferredTargetLanguage: raw.preferredTargetLanguage || 'th-TH',
        translationAudioMode: normalizeTranslationAudioMode(raw.translationAudioMode),
        ...(raw.preferredTtsVoice !== undefined ? { preferredTtsVoice: raw.preferredTtsVoice || null } : {}),
      };

      if (payload.isUpdate) {
        await api.updateCalleeProfile(payload.calleeNumber, profileData);
        if (licenseLevel) {
          await api.updateCalleeLicense(payload.calleeNumber, { licenseLevel });
        }
        return api.getCalleeProfile(payload.calleeNumber);
      }
      const created = await api.createCalleeProfile({
        calleeNumber: payload.calleeNumber,
        ...profileData,
        licenseLevel,
      });

      if (licenseLevel) {
        await api.updateCalleeLicense(payload.calleeNumber, { licenseLevel });
      }

      return created;
    },
    {
      onSuccess: (savedProfile, variables) => {
        queryClient.setQueryData('calleeProfiles', (previous) => {
          if (!previous || !savedProfile) {
            return previous;
          }

          const nextItem = {
            ...savedProfile,
            license: savedProfile.license || { licenseLevel: variables?.data?.licenseLevel || 'AI1' },
          };

          const prevItems = Array.isArray(previous.items) ? previous.items : [];
          const existingIndex = prevItems.findIndex(
            (item) => item.calleeNumber === savedProfile.calleeNumber
          );

          let nextItems;
          if (existingIndex >= 0) {
            nextItems = [...prevItems];
            nextItems[existingIndex] = nextItem;
          } else {
            nextItems = [nextItem, ...prevItems];
          }

          return {
            ...previous,
            items: nextItems,
            count: nextItems.length,
          };
        });

        const savedLicense = savedProfile?.license?.licenseLevel || variables?.data?.licenseLevel || 'AI1';
        const translationEligible = savedProfile?.translationEnabled !== false && savedLicense === 'AI2';

        queryClient.invalidateQueries('calleeProfiles');
        setSnackbar({
          open: true,
          message: `Callee profile saved with ${savedLicense}. Translation ${translationEligible ? 'eligible' : 'not eligible'}.`,
          severity: 'success',
        });
      },
      onError: (error) => {
        const serverMsg = error.response?.data?.error;
        setSnackbar({
          open: true,
          message: serverMsg ? `Save failed: ${serverMsg}` : `Error: ${error.message}`,
          severity: 'error',
        });
      },
    }
  );

  const collectImportProfileLookup = React.useCallback(async (parsedRows) => {
    const calleeNumbers = Array.from(new Set(parsedRows
      .map((parsedRow) => normalizeImportProfile(parsedRow).data.calleeNumber)
      .filter(Boolean)));

    const lookups = await Promise.all(calleeNumbers.map(async (calleeNumber) => {
      if (calleeProfileLookup.has(calleeNumber)) {
        return [calleeNumber, calleeProfileLookup.get(calleeNumber)];
      }

      try {
        return [calleeNumber, await api.getCalleeProfile(calleeNumber)];
      } catch (error) {
        if (error.response?.status === 404) {
          return [calleeNumber, null];
        }
        throw error;
      }
    }));

    return new Map(lookups.filter(([, profile]) => profile));
  }, [calleeProfileLookup]);

  const buildBulkImportRows = React.useCallback((parsedRows, profileLookup = calleeProfileLookup) => parsedRows.map((parsedRow, index) => {
    const normalized = normalizeImportProfile(parsedRow);
    const existing = normalized.data.calleeNumber
      ? profileLookup.get(normalized.data.calleeNumber)
      : null;
    const importedComparable = {
      ...normalized.data,
      preferredTtsVoice: normalized.data.preferredTtsVoice || '',
    };
    const existingComparable = toComparableProfile(existing);
    const changedFields = existing
      ? PROFILE_COMPARISON_FIELDS.filter((field) => importedComparable[field] !== existingComparable[field])
      : [];

    let action = 'CREATE';
    if (normalized.errors.length > 0) {
      action = 'ERROR';
    } else if (existing && changedFields.length === 0) {
      action = 'NO_CHANGE';
    } else if (existing) {
      action = 'UPDATE';
    }

    return {
      id: `${normalized.lineNumber}-${normalized.data.calleeNumber || index}`,
      lineNumber: normalized.lineNumber,
      action,
      selected: ['CREATE', 'UPDATE'].includes(action),
      existing,
      changedFields,
      data: normalized.data,
      errors: normalized.errors,
      status: null,
    };
  }), [calleeProfileLookup]);

  const handleBulkImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      const text = await file.text();
      const parsedRows = parseCsvText(text);
      const profileLookup = await collectImportProfileLookup(parsedRows);
      const reviewRows = buildBulkImportRows(parsedRows, profileLookup);
      setBulkImportFileName(file.name);
      setBulkImportRows(reviewRows);
      setBulkImportReviewOpen(reviewRows.length > 0);
      setSnackbar({
        open: true,
        message: `Loaded ${reviewRows.length} callee profile rows for review.`,
        severity: 'info',
      });
    } catch (error) {
      setBulkImportFileName(file.name);
      setBulkImportRows([]);
      setBulkImportReviewOpen(false);
      setSnackbar({
        open: true,
        message: `CSV import failed: ${error.message}`,
        severity: 'error',
      });
    }
  };

  const toggleBulkImportRow = (rowId) => {
    setBulkImportRows((rows) => rows.map((row) => (
      row.id === rowId && canApplyBulkImportRow(row)
        ? { ...row, selected: !row.selected }
        : row
    )));
  };

  const toggleAllBulkImportRows = (event) => {
    const checked = event.target.checked;
    setBulkImportRows((rows) => rows.map((row) => (
      canApplyBulkImportRow(row) ? { ...row, selected: checked } : row
    )));
  };

  const applyBulkImportRows = async () => {
    const rowsToApply = bulkImportRows.filter((row) => row.selected && canApplyBulkImportRow(row));
    if (rowsToApply.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one create or update row.',
        severity: 'warning',
      });
      return;
    }

    setBulkImportApplying(true);
    let appliedCount = 0;
    let failedCount = 0;

    for (const row of rowsToApply) {
      setBulkImportRows((rows) => rows.map((item) => (
        item.id === row.id ? { ...item, status: 'Applying' } : item
      )));

      try {
        await saveCalleeProfilePayload({
          isUpdate: row.action === 'UPDATE',
          calleeNumber: row.data.calleeNumber,
          data: row.data,
        });
        appliedCount += 1;
        setBulkImportRows((rows) => rows.map((item) => (
          item.id === row.id ? { ...item, status: 'Applied', selected: false } : item
        )));
      } catch (error) {
        failedCount += 1;
        const serverMsg = error.response?.data?.error;
        setBulkImportRows((rows) => rows.map((item) => (
          item.id === row.id ? { ...item, status: serverMsg || error.message } : item
        )));
      }
    }

    await queryClient.invalidateQueries('calleeProfiles');
    setBulkImportApplying(false);
    if (failedCount === 0) {
      setBulkImportReviewOpen(false);
    }
    setSnackbar({
      open: true,
      message: `Bulk import applied ${appliedCount} row${appliedCount === 1 ? '' : 's'}${failedCount ? `, ${failedCount} failed` : ''}.`,
      severity: failedCount ? 'warning' : 'success',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      ...formData,
      scammerThreshold: parseFloat(formData.scammerThreshold),
      translationTranscriptRetentionHours: parseInt(formData.translationTranscriptRetentionHours, 10),
      sessionInactivityTimeoutMinutes: parseInt(formData.sessionInactivityTimeoutMinutes, 10),
      apiTimeout: parseInt(formData.apiTimeout, 10),
      maxReportCount: parseInt(formData.maxReportCount, 10),
    });
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleDiscardChanges = () => {
    if (savedFormData) {
      setFormData(savedFormData);
    }
  };

  const handleDisclaimerFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    uploadDisclaimerMutation.mutate(file);
  };

  const handleProfileChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setProfileForm({ ...profileForm, [field]: value });
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    if (!profileForm.calleeNumber) {
      setSnackbar({
        open: true,
        message: 'Callee number is required',
        severity: 'error',
      });
      return;
    }

    const existing = calleeProfiles.find((item) => item.calleeNumber === profileForm.calleeNumber);
    const data = {
      translationEnabled: profileForm.translationEnabled,
      preferredTargetLanguage: profileForm.preferredTargetLanguage || 'th-TH',
      preferredTtsVoice: profileForm.preferredTtsVoice || null,
      translationAudioMode: profileForm.translationAudioMode,
      licenseLevel: profileForm.licenseLevel,
    };

    saveProfileMutation.mutate({
      isUpdate: Boolean(existing),
      calleeNumber: profileForm.calleeNumber,
      data,
    });
  };

  const startInlineEdit = (profile) => {
    setInlineEditingCallee(profile.calleeNumber);
    setInlineEditForm({
      translationEnabled: profile.translationEnabled !== false,
      preferredTargetLanguage: profile.preferredTargetLanguage || 'th-TH',
      translationAudioMode: normalizeTranslationAudioMode(profile.translationAudioMode),
      licenseLevel: profile.license?.licenseLevel || profile.licenseLevel || 'AI1',
    });
  };

  const cancelInlineEdit = () => {
    setInlineEditingCallee(null);
  };

  const handleInlineEditChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setInlineEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveInlineEdit = (calleeNumber) => {
    saveProfileMutation.mutate(
      {
        isUpdate: true,
        calleeNumber,
        data: {
          translationEnabled: inlineEditForm.translationEnabled,
          preferredTargetLanguage: inlineEditForm.preferredTargetLanguage,
          translationAudioMode: inlineEditForm.translationAudioMode,
          licenseLevel: inlineEditForm.licenseLevel,
        },
      },
      {
        onSuccess: () => {
          setInlineEditingCallee(null);
        },
      }
    );
  };

  const toggleProfileEnabled = (profile) => (event) => {
    saveProfileMutation.mutate({
      isUpdate: true,
      calleeNumber: profile.calleeNumber,
      data: {
        translationEnabled: event.target.checked,
        preferredTargetLanguage: profile.preferredTargetLanguage || 'th-TH',
        translationAudioMode: normalizeTranslationAudioMode(profile.translationAudioMode),
        licenseLevel: profile.license?.licenseLevel || profile.licenseLevel || 'AI1',
      },
    });
  };

  const selectableBulkImportRows = bulkImportRows.filter(canApplyBulkImportRow);
  const selectedBulkImportCount = selectableBulkImportRows.filter((row) => row.selected).length;
  const allBulkImportRowsSelected = selectableBulkImportRows.length > 0
    && selectedBulkImportCount === selectableBulkImportRows.length;
  const bulkImportSummary = {
    creates: bulkImportRows.filter((row) => row.action === 'CREATE').length,
    updates: bulkImportRows.filter((row) => row.action === 'UPDATE').length,
    noChanges: bulkImportRows.filter((row) => row.action === 'NO_CHANGE').length,
    errors: bulkImportRows.filter((row) => row.action === 'ERROR').length,
  };

  if (isLoading || isProfilesLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          System Configuration
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Edit settings as a draft, then select Save Changes. Saved settings apply to new calls.
        </Alert>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Translation Dependency Status
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {summarizeProviderStatus(health).map((item) => (
                  <Chip
                    key={item.key}
                    label={`${item.label}: ${item.status}`}
                    color={getDependencyColor(item.status)}
                    variant={item.status === 'healthy' ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Stack>
              {health?.status && health.status !== 'healthy' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  One or more translation dependencies are degraded. Calls continue using conservative fallback behavior when translation is unavailable.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Appearance
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Theme Profile"
                value={formData.themeProfile}
                onChange={handleChange('themeProfile')}
                helperText={THEME_PROFILE_OPTIONS.find((option) => option.value === formData.themeProfile)?.description}
              >
                {THEME_PROFILE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Detection Settings
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Scammer Threshold"
                type="number"
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                value={formData.scammerThreshold}
                onChange={handleChange('scammerThreshold')}
                helperText="Confidence threshold to classify as scammer (0.0 - 1.0)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Scammer Action"
                value={formData.scammerAction}
                onChange={handleChange('scammerAction')}
                helperText="Action to take when scammer is detected"
              >
                <MenuItem value="BLOCK">Block Call (Reject immediately)</MenuItem>
                <MenuItem value="WHISPER">Whisper Warning (Play warning to recipient)</MenuItem>
                <MenuItem value="DROP">Drop Call (Disconnect recipient)</MenuItem>
                <MenuItem value="LOG_ONLY">Log Only (Allow call)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Whisper Settings
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.whisperEnabled}
                    onChange={handleChange('whisperEnabled')}
                  />
                }
                label="Enable Whisper Warnings"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Whisper Audio Path"
                value={formData.whisperAudioPath}
                onChange={handleChange('whisperAudioPath')}
                helperText="Path to the whisper audio file"
                disabled={!formData.whisperEnabled}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Recording Disclaimer
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.recordingDisclaimerEnabled}
                    onChange={handleChange('recordingDisclaimerEnabled')}
                  />
                )}
                label="Play Recording Disclaimer To Caller"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Applies to new calls after saving.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                component="label"
                type="button"
                variant="outlined"
                startIcon={uploadDisclaimerMutation.isLoading ? <CircularProgress size={18} /> : <UploadFileIcon />}
                disabled={uploadDisclaimerMutation.isLoading}
              >
                Upload Voice File
                <input
                  hidden
                  type="file"
                  accept=".wav,.mp3,.ulaw,.alaw,audio/wav,audio/mpeg,audio/mp3,audio/basic"
                  onChange={handleDisclaimerFileChange}
                />
              </Button>
              {formData.recordingDisclaimerFileName && (
                <Chip
                  label={formData.recordingDisclaimerFileName}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recording Disclaimer Audio Path"
                value={formData.recordingDisclaimerAudioPath}
                onChange={handleChange('recordingDisclaimerAudioPath')}
                disabled={!formData.recordingDisclaimerEnabled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Recording Disclaimer Audio URI"
                value={formData.recordingDisclaimerAudioUri}
                onChange={handleChange('recordingDisclaimerAudioUri')}
                disabled={!formData.recordingDisclaimerEnabled}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Translation Defaults
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Translation Audio Mode"
                value={formData.translationAudioMode}
                onChange={handleChange('translationAudioMode')}
              >
                {TRANSLATION_AUDIO_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Translation Target Language"
                value={formData.translationTargetLanguage}
                onChange={handleChange('translationTargetLanguage')}
              >
                <MenuItem value="th-TH">Thai (th-TH)</MenuItem>
                <MenuItem value="en-US">English (en-US)</MenuItem>
                <MenuItem value="ja-JP">Japanese (ja-JP)</MenuItem>
                <MenuItem value="zh-CN">Chinese (zh-CN)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.translationStoreTranscripts}
                    onChange={handleChange('translationStoreTranscripts')}
                  />
                )}
                label="Store Translation Transcripts"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Transcript Retention (hours)"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={formData.translationTranscriptRetentionHours}
                onChange={handleChange('translationTranscriptRetentionHours')}
                disabled={!formData.translationStoreTranscripts}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Security Settings
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Session Inactivity Timeout (minutes)"
                type="number"
                inputProps={{ min: 0, max: 1440, step: 1 }}
                value={formData.sessionInactivityTimeoutMinutes}
                onChange={handleChange('sessionInactivityTimeoutMinutes')}
                helperText="Web UI signs out idle users after this many minutes. Set 0 to disable."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                API Settings
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Timeout (ms)"
                type="number"
                inputProps={{ min: 1000, step: 1000 }}
                value={formData.apiTimeout}
                onChange={handleChange('apiTimeout')}
                helperText="Timeout for IPQualityScore API requests (ms)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Report Count"
                type="number"
                inputProps={{ min: 0, step: 10 }}
                value={formData.maxReportCount}
                onChange={handleChange('maxReportCount')}
                helperText="Maximum number of reports to consider"
              />
            </Grid>

          </Grid>
        </form>
      </Paper>

      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 24 },
          bottom: 16,
          zIndex: 1200,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          border: 1,
          borderColor: isDirty ? 'warning.main' : 'divider',
        }}
      >
        <Box sx={{ mr: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {isDirty ? 'Unsaved configuration changes' : 'Configuration is saved'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lastSavedAt
              ? `Last saved at ${lastSavedAt.toLocaleTimeString('en-US', { timeZone: 'Asia/Bangkok' })}`
              : 'Saved settings apply to new calls.'}
          </Typography>
        </Box>
        <Button
          type="button"
          variant="text"
          onClick={handleDiscardChanges}
          disabled={!isDirty || updateMutation.isLoading}
        >
          Discard Changes
        </Button>
        <Button
          type="button"
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={!isDirty || updateMutation.isLoading}
        >
          Save Changes
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Admin Password
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              autoComplete="current-password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              autoComplete="new-password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
              helperText="At least 8 characters"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              autoComplete="new-password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              error={passwordMismatch}
              helperText={passwordMismatch ? 'Passwords do not match' : ' '}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="button"
              variant="contained"
              onClick={() => changePasswordMutation.mutate()}
              disabled={
                changePasswordMutation.isLoading ||
                !passwordForm.currentPassword ||
                passwordForm.newPassword.length < 8 ||
                passwordForm.newPassword !== passwordForm.confirmPassword
              }
            >
              Change Password
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Callee Profile Management
        </Typography>
        <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Callee AI Feature Assignment
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Assign an AI license level when saving a callee profile. <strong>AI1</strong> enables whisper only. <strong>AI2</strong> enables whisper and real-time translation.
              </Alert>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Callee Number"
                value={profileForm.calleeNumber}
                onChange={handleProfileChange('calleeNumber')}
                helperText="Example: +66812345678 or SIP extension 6070"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Preferred Target Language"
                value={profileForm.preferredTargetLanguage}
                onChange={handleProfileChange('preferredTargetLanguage')}
              >
                <MenuItem value="th-TH">Thai (th-TH)</MenuItem>
                <MenuItem value="en-US">English (en-US)</MenuItem>
                <MenuItem value="ja-JP">Japanese (ja-JP)</MenuItem>
                <MenuItem value="zh-CN">Chinese (zh-CN)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Preferred TTS Voice"
                value={profileForm.preferredTtsVoice}
                onChange={handleProfileChange('preferredTtsVoice')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                select
                label="AI License Level"
                value={profileForm.licenseLevel}
                onChange={handleProfileChange('licenseLevel')}
                helperText={profileForm.licenseLevel === 'AI1' ? 'AI1 enables whisper only' : 'AI2 enables whisper and real-time translation'}
              >
                <MenuItem value="AI1">AI1 - Whisper only</MenuItem>
                <MenuItem value="AI2">AI2 - Whisper + real-time translation</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Translation Audio Mode"
                value={profileForm.translationAudioMode}
                onChange={handleProfileChange('translationAudioMode')}
              >
                {TRANSLATION_AUDIO_MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={profileForm.translationEnabled}
                    onChange={handleProfileChange('translationEnabled')}
                  />
                )}
                label="Translation Enabled"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  disabled={saveProfileMutation.isLoading}
                  onClick={handleSaveProfile}
                >
                  Save Callee Profile And License
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Bulk Import Callee Profiles
              </Typography>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Button
                  component="label"
                  type="button"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  disabled={bulkImportApplying}
                >
                  Upload CSV
                  <input
                    hidden
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleBulkImportFileChange}
                  />
                </Button>
                <Button
                  component="a"
                  href={BULK_IMPORT_SAMPLE_CSV_HREF}
                  download="callee-profile-import-sample.csv"
                  type="button"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={bulkImportApplying}
                >
                  Sample CSV
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<ReviewIcon />}
                  disabled={bulkImportApplying || bulkImportRows.length === 0}
                  onClick={() => setBulkImportReviewOpen(true)}
                >
                  Review Import
                </Button>
                {bulkImportFileName && (
                  <Chip label={bulkImportFileName} size="small" variant="outlined" />
                )}
                {bulkImportRows.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Create ${bulkImportSummary.creates}`} size="small" color="success" variant="outlined" />
                    <Chip label={`Update ${bulkImportSummary.updates}`} size="small" color="warning" variant="outlined" />
                    <Chip label={`No change ${bulkImportSummary.noChanges}`} size="small" variant="outlined" />
                    <Chip label={`Errors ${bulkImportSummary.errors}`} size="small" color={bulkImportSummary.errors ? 'error' : 'default'} variant="outlined" />
                  </Stack>
                )}
              </Stack>
              {bulkImportApplying && <LinearProgress sx={{ mt: 2 }} />}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Existing Callee Profiles
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Enabled</TableCell>
                      <TableCell>Callee Number</TableCell>
                      <TableCell>Target Language</TableCell>
                      <TableCell>Audio Mode</TableCell>
                      <TableCell>AI License Level</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calleeProfiles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No callee profiles configured yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      calleeProfiles.map((profile) => {
                        const isEditing = inlineEditingCallee === profile.calleeNumber;
                        return (
                          <TableRow key={profile.calleeNumber} hover>
                            <TableCell>
                              {isEditing ? (
                                <Switch
                                  checked={inlineEditForm.translationEnabled}
                                  onChange={handleInlineEditChange('translationEnabled')}
                                />
                              ) : (
                                <Switch
                                  checked={Boolean(profile.translationEnabled)}
                                  onChange={toggleProfileEnabled(profile)}
                                  disabled={saveProfileMutation.isLoading}
                                  inputProps={{ 'aria-label': `Toggle ${profile.calleeNumber}` }}
                                />
                              )}
                            </TableCell>
                            <TableCell>{profile.calleeNumber}</TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  select
                                  value={inlineEditForm.preferredTargetLanguage}
                                  onChange={handleInlineEditChange('preferredTargetLanguage')}
                                >
                                  <MenuItem value="th-TH">Thai (th-TH)</MenuItem>
                                  <MenuItem value="en-US">English (en-US)</MenuItem>
                                  <MenuItem value="ja-JP">Japanese (ja-JP)</MenuItem>
                                  <MenuItem value="zh-CN">Chinese (zh-CN)</MenuItem>
                                </TextField>
                              ) : (
                                profile.preferredTargetLanguage || 'th-TH'
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  select
                                  value={inlineEditForm.translationAudioMode}
                                  onChange={handleInlineEditChange('translationAudioMode')}
                                >
                                  {TRANSLATION_AUDIO_MODE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                  ))}
                                </TextField>
                              ) : (
                                normalizeTranslationAudioMode(profile.translationAudioMode)
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  select
                                  value={inlineEditForm.licenseLevel}
                                  onChange={handleInlineEditChange('licenseLevel')}
                                >
                                  <MenuItem value="AI1">AI1</MenuItem>
                                  <MenuItem value="AI2">AI2</MenuItem>
                                </TextField>
                              ) : (
                                profile.license?.licenseLevel || profile.licenseLevel || 'AI1'
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    type="button"
                                    size="small"
                                    variant="contained"
                                    onClick={() => saveInlineEdit(profile.calleeNumber)}
                                    disabled={saveProfileMutation.isLoading}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    type="button"
                                    size="small"
                                    onClick={cancelInlineEdit}
                                    disabled={saveProfileMutation.isLoading}
                                  >
                                    Cancel
                                  </Button>
                                </Stack>
                              ) : (
                                <Button type="button" size="small" onClick={() => startInlineEdit(profile)}>
                                  Edit
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
      </Paper>

      <Dialog
        open={bulkImportReviewOpen}
        onClose={() => {
          if (!bulkImportApplying) {
            setBulkImportReviewOpen(false);
          }
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Review Callee Profile Import</DialogTitle>
        <DialogContent dividers>
          {bulkImportApplying && <LinearProgress sx={{ mb: 2 }} />}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {bulkImportFileName && (
              <Chip label={bulkImportFileName} size="small" variant="outlined" />
            )}
            <Chip label={`Create ${bulkImportSummary.creates}`} size="small" color="success" variant="outlined" />
            <Chip label={`Existing updates ${bulkImportSummary.updates}`} size="small" color="warning" variant="outlined" />
            <Chip label={`No change ${bulkImportSummary.noChanges}`} size="small" variant="outlined" />
            <Chip label={`Errors ${bulkImportSummary.errors}`} size="small" color={bulkImportSummary.errors ? 'error' : 'default'} variant="outlined" />
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip title="Select all applicable rows">
                      <Checkbox
                        checked={allBulkImportRowsSelected}
                        indeterminate={selectedBulkImportCount > 0 && !allBulkImportRowsSelected}
                        disabled={bulkImportApplying || selectableBulkImportRows.length === 0}
                        onChange={toggleAllBulkImportRows}
                        inputProps={{ 'aria-label': 'Select all import rows' }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>Line</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Callee Number</TableCell>
                  <TableCell>Current</TableCell>
                  <TableCell>Imported</TableCell>
                  <TableCell>Admin Decision</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkImportRows.map((row) => {
                  const current = row.existing ? toComparableProfile(row.existing) : null;
                  const isExistingUpdate = row.action === 'UPDATE';
                  return (
                    <TableRow key={row.id} hover selected={row.selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={row.selected}
                          disabled={bulkImportApplying || !canApplyBulkImportRow(row)}
                          onChange={() => toggleBulkImportRow(row.id)}
                          inputProps={{ 'aria-label': `${isExistingUpdate ? 'Update existing' : 'Apply'} CSV line ${row.lineNumber}` }}
                        />
                      </TableCell>
                      <TableCell>{row.lineNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={isExistingUpdate ? 'EXISTING' : row.action.replace('_', ' ')}
                          size="small"
                          color={BULK_ACTION_COLORS[row.action]}
                          variant={row.action === 'NO_CHANGE' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>{row.data.calleeNumber || '-'}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {current ? (
                          <Stack spacing={0.5}>
                            <Typography variant="body2">License: {current.licenseLevel}</Typography>
                            <Typography variant="body2">Target: {current.preferredTargetLanguage}</Typography>
                            <Typography variant="body2">Enabled: {current.translationEnabled ? 'Yes' : 'No'}</Typography>
                            <Typography variant="body2">Mode: {current.translationAudioMode}</Typography>
                          </Stack>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">License: {row.data.licenseLevel}</Typography>
                          <Typography variant="body2">Target: {row.data.preferredTargetLanguage}</Typography>
                          <Typography variant="body2">Enabled: {row.data.translationEnabled ? 'Yes' : 'No'}</Typography>
                          <Typography variant="body2">Mode: {row.data.translationAudioMode}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {row.errors.length > 0 ? (
                          <Stack spacing={0.5}>
                            {row.errors.map((error) => (
                              <Chip key={error} label={error} color="error" size="small" variant="outlined" />
                            ))}
                          </Stack>
                        ) : isExistingUpdate ? (
                          <Stack spacing={1}>
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  checked={row.selected}
                                  disabled={bulkImportApplying}
                                  onChange={() => toggleBulkImportRow(row.id)}
                                />
                              )}
                              label="Update existing"
                            />
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {row.changedFields.map((field) => (
                                <Chip key={field} label={field} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </Stack>
                        ) : row.changedFields.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {row.changedFields.map((field) => (
                              <Chip key={field} label={field} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No changes
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.status || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => setBulkImportReviewOpen(false)}
            disabled={bulkImportApplying}
          >
            Close
          </Button>
          <Button
            type="button"
            variant="contained"
            startIcon={<ReviewIcon />}
            disabled={bulkImportApplying || selectedBulkImportCount === 0}
            onClick={applyBulkImportRows}
          >
            Apply Selected ({selectedBulkImportCount})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
